// // src/css/ThemeContext.js
// import React, { createContext, useState, useContext, useEffect } from 'react';

// const ThemeContext = createContext();

// export const ThemeProvider = ({ children }) => {
//   const [theme, setTheme] = useState(() => {
//     const savedTheme = localStorage.getItem('customTheme');
//     return savedTheme ? JSON.parse(savedTheme) : {
//       sidebarColor: '#191970', // Midnight Blue
//       accentColor: '#1e3a8a', // Primary Blue
//     };
//   });

//   // Apply theme to CSS variables
//   useEffect(() => {
//     const root = document.documentElement;
//     root.style.setProperty('--theme-sidebar', theme.sidebarColor);
//     root.style.setProperty('--theme-accent', theme.accentColor);
    
//     // Also set derived colors
//     const hexToRgba = (hex, alpha) => {
//       let r = 0, g = 0, b = 0;
      
//       if (hex.length === 4) {
//         r = parseInt(hex[1] + hex[1], 16);
//         g = parseInt(hex[2] + hex[2], 16);
//         b = parseInt(hex[3] + hex[3], 16);
//       } else if (hex.length === 7) {
//         r = parseInt(hex[1] + hex[2], 16);
//         g = parseInt(hex[3] + hex[4], 16);
//         b = parseInt(hex[5] + hex[6], 16);
//       }
      
//       return `rgba(${r}, ${g}, ${b}, ${alpha})`;
//     };

//     // Update derived colors based on accent color
//     if (theme.accentColor.startsWith('#')) {
//       root.style.setProperty('--primary-blue', theme.accentColor);
//       root.style.setProperty('--bg-blue-medium', hexToRgba(theme.accentColor, 0.12));
//       root.style.setProperty('--border-blue', hexToRgba(theme.accentColor, 0.28));
//       root.style.setProperty('--glow-blue', hexToRgba(theme.accentColor, 0.45));
//     }
//   }, [theme]);

//   const updateTheme = (newTheme) => {
//     setTheme(newTheme);
//     localStorage.setItem('customTheme', JSON.stringify(newTheme));
//   };

//   const updateSidebarColor = (color) => {
//     const updatedTheme = {
//       ...theme,
//       sidebarColor: color,
//     };
//     updateTheme(updatedTheme);
//   };

//   const updateAccentColor = (color) => {
//     const updatedTheme = {
//       ...theme,
//       accentColor: color,
//     };
//     updateTheme(updatedTheme);
//   };

//   const resetTheme = () => {
//     const defaultTheme = {
//       sidebarColor: '#191970',
//       accentColor: '#1e3a8a',
//     };
//     updateTheme(defaultTheme);
//   };

//   return (
//     <ThemeContext.Provider value={{
//       theme,
//       updateTheme,
//       updateSidebarColor,
//       updateAccentColor,
//       resetTheme
//     }}>
//       {children}
//     </ThemeContext.Provider>
//   );
// };

// export const useTheme = () => useContext(ThemeContext);





// src/context/ThemeContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const defaultTheme = {
  sidebarType: 'gradient', // 'solid' | 'gradient'
  sidebarColor: '#191970',
  sidebarGradient: { start: '#191970', end: '#0f3460' },
  appGradient: { start: '#1e3a8a', end: '#3b82f6' }
};

export const generateGradient = (start, end, angle = 135) =>
  `linear-gradient(${angle}deg, ${start} 0%, ${end} 100%)`;

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('appTheme');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure structure matches default
        return {
          ...defaultTheme,
          ...parsed,
          sidebarGradient: { ...defaultTheme.sidebarGradient, ...(parsed.sidebarGradient || {}) },
          appGradient: { ...defaultTheme.appGradient, ...(parsed.appGradient || {}) },
        };
      } catch (e) {
        console.warn('Invalid theme in localStorage, resetting...');
      }
    }
    return defaultTheme;
  });

  const updateCSS = useCallback((t) => {
    const root = document.documentElement;

    // Sidebar background
    const sidebarBg = t.sidebarType === 'gradient'
      ? generateGradient(t.sidebarGradient.start, t.sidebarGradient.end)
      : t.sidebarColor;
    root.style.setProperty('--theme-sidebar', sidebarBg);

    // Application accent (navbar, buttons, etc.)
    const appBg = generateGradient(t.appGradient.start, t.appGradient.end);
    root.style.setProperty('--theme-accent', appBg);
    root.style.setProperty('--theme-accent-solid', t.appGradient.start);
  }, []);

  useEffect(() => {
    updateCSS(theme);
    localStorage.setItem('appTheme', JSON.stringify(theme));
  }, [theme, updateCSS]);

  const updateSidebarType = (type) => {
    setTheme(prev => ({ ...prev, sidebarType: type }));
  };

  const updateSidebarSolid = (color) => {
    setTheme(prev => ({
      ...prev,
      sidebarType: 'solid',
      sidebarColor: color,
      sidebarGradient: { start: color, end: color }
    }));
  };

  const updateSidebarGradient = (start, end) => {
    setTheme(prev => ({
      ...prev,
      sidebarType: 'gradient',
      sidebarGradient: { start, end }
    }));
  };

  const updateAppGradient = (start, end) => {
    setTheme(prev => ({
      ...prev,
      appGradient: { start, end }
    }));
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      updateSidebarType,
      updateSidebarSolid,
      updateSidebarGradient,
      updateAppGradient,
      resetTheme,
      generateGradient
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};